import React, {Fragment, useEffect, useState} from 'react';
import Slider from '@mui/material/Slider';
import {useTranslation} from 'react-i18next';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {user} from '../../store/reducers/user';
import {mapObservationFromAPI, mapObservationToAPI, obsStatuses} from '../../pages/ward/forms/observation/obsUtils';
import {addNoteForPatient} from '../../store/reducers/notes';
import API from '../../app/api';
import _ from 'lodash';
import Notes from '../../util/notes';
import Formatter from '../../util/formatter';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import makeStyles from '@mui/styles/makeStyles';
import AgyleButton from '../ui/buttons/agyleButton';
import {getConfig} from '../../util/configUtil';
import {getAdmissionStartDate} from '../../util/patientUtil';
import CloseIcon from '@mui/icons-material/Close';
import {refreshPatient} from '../../store/reducers/patient';

// To show marks on slider
const marks = [
    {
        value: 0,
        label: '0',
    },
    {
        value: 1,
        label: '1',
    },
    {
        value: 2,
        label: '2',
    },
    {
        value: 3,
        label: '3',
    },
    {
        value: 4,
        label: '4',
    },
    {
        value: 5,
        label: '5',
    },
    {
        value: 6,
        label: '6',
    },
    {
        value: 7,
        label: '7',
    },
    {
        value: 8,
        label: '8',
    },
    {
        value: 9,
        label: '9',
    },
    {
        value: 10,
        label: '10',
    },
];

const useStyles = makeStyles((theme) => ({
    consciousnessLevelChips: {
        textAlign: 'center',
        padding: '8px 0px',
    },
    consciousnessLevelChip: {
        margin: 2,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
}));

/**
 * Pain Score components to use in mini forms
 * @return JSX component
 */
const PainScore = ({patient, value = 0, obsId = '', disabled = false, onChange, onSaveObs}) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);
    const {t} = useTranslation();

    const controller = new AbortController();

    const [cloneValue, setCloneValue] = useState(typeof value === 'number' ? value : Number(value));
    const [obs, setObs] = useState(null);

    const [isLoading, setIsLoading] = useState(false);

    const [mode, setMode] = useState(0);

    const age = patient ? moment(getAdmissionStartDate(patient?.admission)).diff(patient?.patient?.dob, 'years') : null;

    // Configs
    const painScoreAdditionalButtons = getConfig('observations.painScoreAdditionalButtons', false);
    const dateFormat = getConfig('defaultDateTimeFormat');

    useEffect(() => {
        if (patient && obsId && obsId !== '') {
            API.getFullObservations(patient, age >= 18 ? 'news' : 'pews', true, controller.signal)
                .then((result) => {
                    const specObs = _.find(result, {__id: obsId});
                    if (specObs) {
                        setObs(mapObservationFromAPI(specObs));
                        setMode(2);
                    }
                });
        }
        return () => {
            controller.abort();
        };
    }, []);

    const saveObs = async () => {
        if (patient) {
            let ptObject = await dispatch(refreshPatient(patient));
            setIsLoading(true);
            API.saveObservation({
                patientId: ptObject.patient?.__id,
                observation: createObsObject(),
            }).then((apiObs) => {
                let obs = mapObservationFromAPI(apiObs);

                if (onSaveObs) {
                    onSaveObs({
                        success: true,
                        obsId: obs.__id,
                    });
                }

                saveNotes(obs, ptObject);
                setObs(obs);
            }).catch(error => {
                console.error('Pain score save error', error);
            }).finally(() => {
                setIsLoading(false);
                setMode(2);
            });
        }
    };

    const saveNotes = (obs, patient) => {
        // build a note content
        const note = Notes.getPainScoreNote(cloneValue);

        dispatch(addNoteForPatient(patient?.patient?.__id, {
            note: note,
            noteType: 'obs',
            patientsInWardsRef: [patient?.__id],
            referenceId: obs.__id,
            episodeId: patient?.admission?.consultantEpisodeId || null,
        }, patient.__id));
    };

    const createObsObject = () => {
        const date = moment();

        const obj = {
            type: age < 18 ? 'pews' : 'news',
            startedDate: date,
            completedDate: date,
            completedByFullName: tester,
            completedBy: userObject?.userName,
            status: obsStatuses.partial,
            canceledDate: null,
            canceledByFullName: null,
            canceledBy: null,
            totalScore: 0,
            nextObs: null,
            nextObsDate: null,
            nextObsStatus: obsStatuses.new,
            nextObsReason: '',
            clinicalResponse: null,
            riskLevel: null,
            prevTotalScore: null,
            prevRiskLevel: null,
            extraOption1: null,
            extraOption2: null,
            extraOption3: null,
            extraOption3Reason: null,
            attendanceCustomId: patient?.admission?.attendanceCustomId,
            patientInWardId: patient.__id,
            painScore: {
                status: obsStatuses.completed,
                score: 0,
                accepted: true,
                acceptDate: date,
                painScore: cloneValue,
            },
            respiration: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                breaths: undefined,
            },
            distress: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                distress: null,
            },
            wheezing: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                wheezing: null,
            },
            stridor: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                stridor: null,
            },
            apnoea: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                apnoea: null,
            },
            airOxygen: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                airType: 'room',
                device: null,
                inspiredOxygen: undefined,
            },
            spo2scale: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                hypercapnic: false,
                spo2scale: undefined,
            },
            bloodPressure: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                systolicPressure: undefined,
                diastolicPressure: undefined,
                arm: 'left',
                optionalBP: false,
                optionalSystolicPressure: undefined,
                optionalDiastolicPressure: undefined,
                optionalArm: 'left',
            },
            pulseRate: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                pulseRate: undefined,
                pulseType: undefined,
            },
            consciousness: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                consciousnessLevel: null,
            },
            temperature: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                temperature: undefined,
            },
            crt: {
                status: obsStatuses.notRecorded,
                score: 0,
                accepted: false,
                acceptDate: null,
                crt: undefined,
            },
            eyesOpen: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                eyesOpenLevel: null,
                eyesClosedBySwelling: false,
            },
            bestVerbalResponse: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                response: null,
                tracheostomy: false,
            },
            bestMotorResponse: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                response: null,
            },
            pupils: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                reactionLeft: null,
                sizeLeft: undefined,
                closedLeft: false,
                reactionRight: null,
                sizeRight: undefined,
                closedRight: false,
            },
            limbMovementArms: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                movement: null,
                optionalArm: false,
                arm: null,
                secondMovement: null,
                secondArm: null,
            },
            limbMovementLegs: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                movement: null,
                optionalLeg: false,
                leg: null,
                secondMovement: null,
                secondLeg: null,
            },
            glucose: {
                status: obsStatuses.notRecorded,
                score: undefined,
                accepted: false,
                acceptDate: null,
                glucose: undefined,
            },
        };

        return mapObservationToAPI(obj);
    };

    const handleSliderChange = (event, newValue) => {
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleChangeScore = (val) => {
        setCloneValue(val);
        if (onChange) {
            onChange(val);
        }
    };

    return (
        <>
            {mode === 0 &&
                <AgyleButton
                    style={{width: '100%'}}
                    variant={'contained'}
                    disabled={disabled}
                    onClick={() => setMode(1)}
                >
                    {t('Add Pain Score')}
                </AgyleButton>
            }

            {mode === 1 &&
                <div>
                    <b>{t('Pain Score')}</b>
                    <Slider
                        value={cloneValue}
                        disabled={disabled || isLoading}
                        valueLabelDisplay="auto"
                        step={1}
                        marks={marks}
                        min={0}
                        max={10}
                        onChangeCommitted={handleSliderChange}
                        onChange={event => setCloneValue(event.target.value)}
                    />

                    {painScoreAdditionalButtons &&
                        <Grid item xs={12} className={classes.consciousnessLevelChips}>
                            <Chip
                                className={classes.consciousnessLevelChip}
                                label="None"
                                onClick={handleChangeScore.bind(this, 0)}
                                variant="outlined"
                            />
                            <Chip
                                className={classes.consciousnessLevelChip}
                                label="Mild"
                                onClick={handleChangeScore.bind(this, 3)}
                                variant="outlined"
                            />
                            <Chip
                                className={classes.consciousnessLevelChip}
                                label="Moderate"
                                onClick={handleChangeScore.bind(this, 7)}
                                variant="outlined"
                            />
                            <Chip
                                className={classes.consciousnessLevelChip}
                                label="Severe"
                                onClick={handleChangeScore.bind(this, 10)}
                                variant="outlined"
                            />
                        </Grid>
                    }

                    <div className={classes.divRow}>
                        <AgyleButton
                            startIcon={<CloseIcon/>}
                            onClick={() => setMode(0)}
                            variant="contained"
                        >
                            {t('Cancel')}
                        </AgyleButton>

                        <LoadingButton
                            style={{width: '100%'}}
                            variant="contained"
                            disabled={disabled}
                            onClick={saveObs}
                            loading={isLoading}
                            loadingPosition="center"
                        >
                            {t('Submit Pain Score')}
                        </LoadingButton>
                    </div>
                </div>
            }

            {mode === 2 &&
                <Button
                    style={{width: '100%'}}
                    variant="contained"
                    color="success"
                    disableRipple
                >
                    {t(`Pain Score successfully completed. Score: ${!_.isNil(obs?.painScore?.painScore) ? obs?.painScore?.painScore : '-'} 
                    completed at ${obs.completedDate ? moment(obs.completedDate).format(dateFormat) : '-'} 
                    by ${obs?.completedByFullName ? obs.completedByFullName : '-'}`)}
                </Button>
            }
        </>
    );
};

export default PainScore;